import client from './client';
import { Trip, TripFuelDetails } from '../types/entities';
import { ApiResponse, CreateTripRequest } from '../types/api';

interface TripFilters {
  startDate?: string;
  endDate?: string;
  vehicleId?: number;
  supplierId?: number;
  customerId?: number;
  driverId?: number;
  // New flexible trip filters
  tripType?: 'IMPORT' | 'EXPORT';
  pickupType?: 'SUPPLIER' | 'CUSTOMER' | 'WAREHOUSE';
  deliveryType?: 'SUPPLIER' | 'CUSTOMER' | 'WAREHOUSE';
}

// Flexible trip creation types
export interface PickupDeliveryInfo {
  type: 'SUPPLIER' | 'CUSTOMER' | 'WAREHOUSE';
  id: number;
  addressBookId?: number;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
}

export interface TripItemRequest {
  itemId?: number;
  itemName: string;
  volumeWeight: number;
  unit: string;
  unitPrice: number;
  purchasePrice?: number;
  salePrice?: number;
  isGiftFromSupplier?: boolean;
  isGiftToCustomer?: boolean;
}

export interface FlexibleTripRequest {
  date: string;
  vehicleId: number;
  driverId?: number;
  distanceKm?: number;
  driverWage?: number;
  tripType: 'IMPORT' | 'EXPORT';
  pickup: PickupDeliveryInfo;
  delivery: PickupDeliveryInfo;
  // Chuyến không trực tiếp: dùng items (backward compat)
  items?: TripItemRequest[];
  // Chuyến trực tiếp SUPPLIER→CUSTOMER: dùng hai danh sách riêng biệt
  purchaseItems?: TripItemRequest[];  // Hàng nhập từ NCC
  deliveryItems?: TripItemRequest[];  // Hàng giao cho KH
}

export const tripAPI = {
  getAll: async (filters?: TripFilters): Promise<Trip[]> => {
    const response = await client.get('/trips', { params: filters });
    return response.data.data;
  },

  getById: async (id: number): Promise<Trip> => {
    const response = await client.get(`/trips/${id}`);
    return response.data.data;
  },

  // Legacy create (backward compatible)
  create: async (data: CreateTripRequest): Promise<Trip> => {
    const response = await client.post('/trips', data);
    return response.data.data;
  },

  // New flexible create with pickup/delivery objects
  createFlexible: async (data: FlexibleTripRequest): Promise<Trip> => {
    const response = await client.post('/trips', data);
    return response.data.data;
  },

  update: async (id: number, data: Partial<CreateTripRequest> | FlexibleTripRequest): Promise<Trip> => {
    const response = await client.put(`/trips/${id}`, data);
    return response.data.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await client.delete(`/trips/${id}`);
    return response.data;
  },

  // Get fuel consumption details for a trip
  getFuelDetails: async (id: number): Promise<TripFuelDetails> => {
    const response = await client.get(`/trips/${id}/fuel`);
    return response.data.data;
  },

  // Phase 2: Complete trip with warehouse receipt confirmation
  // leftoverWarehouseId: for direct trips, enter leftover goods into this warehouse
  complete: async (
    id: number,
    receiptToWarehouse: boolean,
    leftoverWarehouseId?: number
  ): Promise<Trip> => {
    const response = await client.post(`/trips/${id}/complete`, {
      receiptToWarehouse,
      leftoverWarehouseId,
    });
    return response.data.data;
  },

  // Phase 2: Receipt trip to warehouse (deferred receipt)
  receiptToWarehouse: async (id: number): Promise<Trip> => {
    const response = await client.post(`/trips/${id}/receipt-to-warehouse`);
    return response.data.data;
  },

  // Phase 2: Get trips pending warehouse receipt
  getPendingReceipt: async (): Promise<{ data: Trip[]; count: number }> => {
    const response = await client.get('/trips/pending-receipt');
    return {
      data: response.data.data,
      count: response.data.count,
    };
  },
};
